/* Start Here page: one area panel at a time, picked through ?category=<area>.

   The markup prints every panel open. Two small scripts in the section set the
   collapsed state for the first paint; this element takes over from there and
   keeps the address, the browser history, the breadcrumb and the marked card
   in step with the picked panel. */
if (!customElements.get('fuel06-start-here')) {
  customElements.define(
    'fuel06-start-here',
    class extends HTMLElement {
      connectedCallback() {
        this.areas = [...this.querySelectorAll('[data-fuel06-area]')];
        this.panels = [...this.querySelectorAll('[data-fuel06-panel]')];
        this.crumbs = [...this.querySelectorAll('[data-fuel06-crumb]')];
        this.keys = this.panels.map((panel) => panel.dataset.fuel06Panel);
        this.wide = window.matchMedia('(min-width: 1024px)');
        this.calm = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.onClick = this.onClick.bind(this);
        this.onPop = this.onPop.bind(this);
        this.addEventListener('click', this.onClick);
        window.addEventListener('popstate', this.onPop);

        this.classList.add('is-ready');
        const key = this.keyFromUrl();
        this.show(key);
        /* Arriving from a home page tile on a phone, the picked panel sits
           under the four cards. Bring it up on the next frame, and only if the
           visitor has not scrolled yet. Pictures carry their own size, so the
           layout is already final. */
        if (key && !this.wide.matches) {
          requestAnimationFrame(() => {
            if (window.scrollY < 8) this.bringUp(this.panelFor(key));
          });
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        window.removeEventListener('popstate', this.onPop);
      }

      keyFromUrl() {
        const key = new URLSearchParams(window.location.search).get('category');
        return this.keys.includes(key) ? key : null;
      }

      panelFor(key) {
        return this.panels.find((panel) => panel.dataset.fuel06Panel === key);
      }

      onClick(event) {
        if (event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const area = event.target.closest('[data-fuel06-area]');
        const all = event.target.closest('[data-fuel06-all]');
        if (!area && !all) return;
        event.preventDefault();

        const key = area ? area.dataset.fuel06Area : null;
        const previous = this.current;
        if (key === previous) {
          /* The open area again: show it again. Step 1 while on step 1: nothing to do. */
          if (key) this.reveal(key, previous);
          return;
        }
        const url = new URL(window.location.href);
        if (key) url.searchParams.set('category', key);
        else url.searchParams.delete('category');
        window.history.pushState({}, '', url);
        this.show(key);
        this.reveal(key, previous);
      }

      /* Browser back and forward: the browser restores the scroll position, focus follows the state. */
      onPop() {
        const previous = this.current;
        const key = this.keyFromUrl();
        if (key === previous) return;
        this.show(key);
        this.focusFor(key, previous);
      }

      show(key) {
        this.current = key;
        this.classList.toggle('is-picked', Boolean(key));
        for (const panel of this.panels) panel.classList.toggle('is-active', panel.dataset.fuel06Panel === key);
        for (const crumb of this.crumbs) crumb.classList.toggle('is-active', crumb.dataset.fuel06Crumb === key);
        for (const area of this.areas) {
          const picked = area.dataset.fuel06Area === key;
          area.classList.toggle('is-selected', picked);
          if (picked) area.setAttribute('aria-current', 'true');
          else area.removeAttribute('aria-current');
        }
      }

      /* The panel title when an area opens, the card that was open when the visitor is back at step 1. */
      focusFor(key, previous) {
        const target = key
          ? this.panelFor(key)?.querySelector('[data-fuel06-heading]')
          : this.areas.find((area) => area.dataset.fuel06Area === previous) ?? this.areas[0];
        target?.focus({ preventScroll: true });
      }

      /* The theme header sticks to the top at a height only known at runtime,
         so a scroll target has to leave that much room above itself. */
      bringUp(target, behavior = 'auto') {
        if (!target) return;
        const header = document.querySelector('.section-header');
        const stuck = header && getComputedStyle(header).position === 'sticky' ? header.offsetHeight : 0;
        target.style.scrollMarginTop = `${stuck + 16}px`;
        target.scrollIntoView({ block: 'start', behavior });
      }

      /* After a click: move focus to what just appeared and bring it on screen. */
      reveal(key, previous) {
        this.focusFor(key, previous);
        this.bringUp(key && !this.wide.matches ? this.panelFor(key) : this, this.calm.matches ? 'auto' : 'smooth');
      }
    }
  );
}
